import publicSoundCount from './public-sound-count.json'
import { roundDownToPublicCount } from './public-sound-count-snapshot'

const PUBLIC_SOUND_COUNT = publicSoundCount.count
export const PUBLIC_SOUND_COUNT_LABEL = publicSoundCount.display
export const STALE_PUBLIC_SOUND_COUNT_LABELS = publicSoundCount.stale

/**
 * Below this, a count is too small to floor — rounding 7 down to the nearest 10
 * would render "0+". Catalog-scale numbers round; small ones stay exact.
 */
const PUBLIC_COUNT_ROUNDING_FLOOR = 100

export function formatPublicSoundCount(count: number = PUBLIC_SOUND_COUNT) {
  return `${count.toLocaleString('en-US')}+`
}

/**
 * Turn a live count into the public label.
 *
 * TES-1554: catalog-scale counts are floored to the nearest 10 — the same
 * transform scripts/refresh-public-sound-count.ts applies to the snapshot — so
 * live-fetching pages and snapshot pages render one identical number sitewide
 * instead of "1,392+" next to "1,390+". It also keeps a rendered label from
 * ever colliding with a retired one: the refresher retires the outgoing label
 * onto the `stale` reject list, and an unrounded live count passing through
 * that exact value would fail the deploy gate against correct HTML.
 */
export function getPublicSoundCountLabel(count?: number | null) {
  if (typeof count !== 'number' || count <= 0) return PUBLIC_SOUND_COUNT_LABEL
  return formatPublicSoundCount(
    count >= PUBLIC_COUNT_ROUNDING_FLOOR ? roundDownToPublicCount(count) : count,
  )
}

/**
 * A catalog-scale public count token, e.g. "1,580+".
 *
 * The thousands separator is required, not incidental: it is what keeps this
 * from swallowing an ordinary "5+" or "12+" elsewhere in the same sentence.
 * The catalog passed 1,000 in 2026 and the label is floored, so every real
 * token this needs to match carries a comma.
 */
export const CATALOG_COUNT_TOKEN_RE = /\d{1,3}(?:,\d{3})+\+/g

/** Every catalog-scale count token in `text`, in render order, with duplicates. */
export function extractCatalogCountTokens(text: string): string[] {
  return text.match(CATALOG_COUNT_TOKEN_RE) ?? []
}

/**
 * Rewrite every catalog-scale count token in `text` to `liveLabel` (TES-1801).
 *
 * The site renders the public count two ways and cannot render it one way. Pages
 * that can `await` (`/`, `/sounds`, `/guide`, `/faq`) fetch the live `is_active`
 * count every revalidate. Blog posts under `content/blog/` interpolate
 * `PUBLIC_SOUND_COUNT_LABEL` at *module scope*, so their copy is frozen at build
 * time and only moves when scripts/refresh-public-sound-count.ts runs. Both are
 * deliberate; neither is going away.
 *
 * The bug is what happens when a live page borrows frozen copy. `/sounds` renders
 * its own header from the live count and six blog teasers from the frozen one, so
 * on 2026-08-20 it showed "1,580+" above the fold and "Browse 1,330+ free Tesla
 * lock sounds" in the SEO tail — a 16% self-contradiction on the page that takes
 * ~36% of landings and ~70% of download sessions.
 *
 * So: wherever a surface already holds the authoritative live label, no borrowed
 * token is allowed to survive next to it. The live label wins by construction,
 * which is stronger than refreshing the snapshot more often — a refresh narrows
 * the window, this closes it.
 */
export function alignSoundCountCopy(text: string, liveLabel?: string | null): string {
  if (!liveLabel || !text) return text
  return text.replace(CATALOG_COUNT_TOKEN_RE, liveLabel)
}
